import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useAuth } from "../contexts/AuthContext";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { fetchTasksForAccount } from "../shared/api/tasks";
import { computeTimePhased } from "../shared/utils/taskFilters";
import {
  getTaskScheduleStatusLabel,
  getTaskScheduleStatusSortRank,
  scheduleStatusLabelToParam,
  SCHEDULE_STATUS_COLORS,
  taskHasTimeBlocks,
} from "../shared/utils/taskScheduleStatus";
import { serverInfo } from "../shared/config/serverConfig";
import ErrorBanner from "../components/ErrorBanner";
import TaskSectionTabs, { TASK_TAB_DASHBOARD } from "../components/TaskSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import "./Dashboard.css";


import { apiFetch } from "../shared/api/apiFetch";

const VIEWS = [
  { id: "all", label: "Overview" },
  { id: "category", label: "By Category" },
  { id: "project", label: "By Project" },
  { id: "owner", label: "By Owner" },
  { id: "schedule_status", label: "By Schedule Status" },
  { id: "vendor", label: "By Vendor" },
];

function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSimpleView = searchParams.get("view") === "simple";
  const { userInfo, loading: authLoading, authError } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authSettled, setAuthSettled] = useState(false);
  const [showMissingAccount, setShowMissingAccount] = useState(false);
  const [dashboardView, setDashboardView] = useState(isSimpleView ? "schedule_status" : "all");
  const [vendorList, setVendorList] = useState([]);
  const fetchIdRef = useRef(0);

  const DASH_TABS = [
    { id: "overview", label: "Overview" },
    { id: "time_blocking", label: "Time blocking" },
  ];

  const tabFromUrl = searchParams.get("tab");
  const activeTab = DASH_TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : "time_blocking";

  useEffect(() => {
    if (!tabFromUrl || !DASH_TABS.some((t) => t.id === tabFromUrl)) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keep URL in sync
  }, [tabFromUrl, activeTab, setSearchParams]);

  /**
   * Navigate to View Task with filter context so counts match the clicked bar (Tasks Snapshot chart + Focus panel).
   *
   * New model:
   * - Global time navigator is the only time control -> we pass `navView` + `navDate`
   * - Open/Closed is separate -> we pass `status=open|closed`
   */
  function handleDrilldown(bucketKey, groupNameOrKey) {
    const params = new URLSearchParams();
    const todayIso = dayjs().format("YYYY-MM-DD");
    params.set("navView", bucketKey === "closed" ? "all" : bucketKey === "remaining" ? "future" : "month");
    if (bucketKey === "remaining") {
      params.set("navDate", todayIso);
      params.set("status", "open");
    } else if (bucketKey === "closed") {
      params.set("navDate", todayIso);
      params.set("status", "closed");
    } else {
      // "upcoming" => current month
      params.set("navDate", todayIso);
      params.set("status", "open");
    }
    if (groupNameOrKey != null && groupNameOrKey !== "") {
      if (dashboardView === "project") params.set("project", groupNameOrKey);
      else if (dashboardView === "category") params.set("category", groupNameOrKey);
      else if (dashboardView === "owner") params.set("owner", groupNameOrKey === "Unassigned" ? "__unassigned__" : groupNameOrKey);
      else if (dashboardView === "schedule_status") {
        params.set("scheduleStatus", scheduleStatusLabelToParam(String(groupNameOrKey)));
      } else if (dashboardView === "vendor") params.set("vendor", groupNameOrKey === "No vendor" ? "__none__" : groupNameOrKey);
    }
    navigate("/Task?" + params.toString());
  }

  const account_id = userInfo?.account_id;

  useEffect(() => {
    if (authLoading) {
      setAuthSettled(false);
      setShowMissingAccount(false);
      return;
    }
    const t1 = setTimeout(() => setAuthSettled(true), 150);
    const t2 = setTimeout(() => setShowMissingAccount(true), 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [authLoading]);

  useEffect(() => {
    if (authLoading) return;
    if (!account_id) {
      setLoading(false);
      return;
    }

    setError("");
    const id = ++fetchIdRef.current;
    fetchTasksForAccount(account_id, { user_id: userInfo?._id || "", role: userInfo?.role || "" })
      .then((data) => {
        if (id !== fetchIdRef.current) return;
        setItems(Array.isArray(data) ? data : []);
        setError("");
        setLoading(false);
      })
      .catch((err) => {
        if (id !== fetchIdRef.current) return;
        setError(
          getMessageForFetchError(
            err,
            "Unable to load dashboard data. Please try again."
          )
        );
        setLoading(false);
      });
  }, [account_id, authLoading, userInfo?._id, userInfo?.role]);

  useEffect(() => {
    if (!account_id || dashboardView !== "vendor") {
      setVendorList([]);
      return;
    }
    const url = serverInfo.serverUrl + "/listVendors";
    apiFetch(url, {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setVendorList(Array.isArray(data) ? data : []))
      .catch(() => setVendorList([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when account_id or view changes
  }, [account_id, dashboardView]);

  const missingAccountMessage =
    !authLoading && authSettled && showMissingAccount && !account_id
      ? (authError || "Missing account information. Please log in again.")
      : "";
  const displayError = error || missingAccountMessage;

  const today = dayjs().startOf("day");
  const monthStart = today.startOf("month").startOf("day");
  const monthEnd = today.endOf("month").endOf("day");

  const formatAmount = (n) =>
    Number(n).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const allStats = computeTimePhased(items, monthStart, monthEnd);
  const closedCount = allStats.closedCount;
  const upcomingCount = allStats.upcomingCount;
  const remainingCount = allStats.remainingCount;
  const closedAmount = allStats.closedAmount;
  const upcomingAmount = allStats.upcomingAmount;
  const remainingAmount = allStats.remainingAmount;

  const totalCount = closedCount + upcomingCount + remainingCount;
  const totalAmount = closedAmount + upcomingAmount + remainingAmount;

  /** Single source of truth: closed / upcoming / remaining use these colors everywhere (bars, legend, Focus panel). */
  const barColors = {
    closed: "#8CD17D",   // Light Green
    upcoming: "#FFBE7D", // Light Orange
    remaining: "#A0CBE8", // Light Blue
  };

  /** Indigo palette to match Closed Events Metrics (stacked bars). */
  const GROUP_PALETTE = [
    "#312e81", "#4f46e5", "#6366f1", "#818cf8",
    "#a5b4fc", "#c7d2fe", "#4c1d95", "#5b21b6",
  ];

  /** Time Blocking "card" base color (used as gradient base in simple dashboard).
   *  Matches the dark teal/blue card UI (not the electric purple accent used for some lists).
   */
  const TIME_BLOCKING_CARD_BASE = "#004B7D"; // matches Next Action button/card accent on Dashboard

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function hexToHsl(hex) {
    const cleaned = String(hex).replace("#", "").trim();
    if (cleaned.length !== 6) return { h: 0, s: 0, l: 50 };
    const r = parseInt(cleaned.slice(0, 2), 16) / 255;
    const g = parseInt(cleaned.slice(2, 4), 16) / 255;
    const b = parseInt(cleaned.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (d !== 0) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        default:
          h = (r - g) / d + 4;
          break;
      }
      h *= 60;
    }

    return { h, s: s * 100, l: l * 100 };
  }

  function hslToHex(h, s, l) {
    const hh = ((h % 360) + 360) % 360;
    const ss = clamp(s, 0, 100) / 100;
    const ll = clamp(l, 0, 100) / 100;

    const c = (1 - Math.abs(2 * ll - 1)) * ss;
    const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
    const m = ll - c / 2;

    let r1 = 0;
    let g1 = 0;
    let b1 = 0;
    if (hh < 60) {
      r1 = c; g1 = x; b1 = 0;
    } else if (hh < 120) {
      r1 = x; g1 = c; b1 = 0;
    } else if (hh < 180) {
      r1 = 0; g1 = c; b1 = x;
    } else if (hh < 240) {
      r1 = 0; g1 = x; b1 = c;
    } else if (hh < 300) {
      r1 = x; g1 = 0; b1 = c;
    } else {
      r1 = c; g1 = 0; b1 = x;
    }

    const toHex = (v) => clamp(Math.round((v + m) * 255), 0, 255).toString(16).padStart(2, "0");
    return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
  }

  function buildGradientColors(baseHex, count) {
    if (!count || count <= 0) return [];
    if (count === 1) return [baseHex];

    const base = hexToHsl(baseHex);
    // Generate a single-hue ramp so we don't end up with many distinct colors.
    // Keep the lightness range relatively narrow to avoid "patchy" contrast.
    const lMin = 44;
    const lMax = 66;

    return Array.from({ length: count }).map((_, idx) => {
      const t = idx / (count - 1); // 0 -> first (lighter), 1 -> last (darker)
      const l = lMax - (lMax - lMin) * t;
      // Slightly reduce saturation so the ramp reads as "same theme" even for many groups.
      const s = clamp(base.s * 0.85, 0, 100);
      return hslToHex(base.h, s, l);
    });
  }

  const chartDataAll = [
    { key: "upcoming", label: "Current Month", value: upcomingCount, amount: upcomingAmount, color: barColors.upcoming },
    { key: "remaining", label: "Beyond Current Month", value: remainingCount, amount: remainingAmount, color: barColors.remaining },
    { key: "closed", label: "Closed", value: closedCount, amount: closedAmount, color: barColors.closed },
  ];

  function buildGroupedCharts(groupKey) {
    const groups = {};
    const isVendor = groupKey === "vendor";
    const vendorById = isVendor ? Object.fromEntries((vendorList || []).map((v) => [String(v._id), v])) : {};
    items.forEach((item) => {
      const key = groupKey === "category"
        ? (item.category || "General")
        : groupKey === "project"
          ? (item.project || "Others")
          : groupKey === "owner"
            ? (item.owner || "Unassigned")
            : groupKey === "schedule_status"
              ? getTaskScheduleStatusLabel(item)
            : isVendor
                ? (item.vendor_id && vendorById[String(item.vendor_id)] ? String(item.vendor_id) : "__none__")
                : (item.vendor_id || "");
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return Object.entries(groups)
      .map(([idOrName, groupItems]) => {
        const s = computeTimePhased(groupItems, monthStart, monthEnd);
        const displayName = isVendor
          ? (idOrName === "__none__" ? "No vendor" : (vendorById[idOrName]?.name || "No vendor"))
          : idOrName;
        const drilldownKey = isVendor ? idOrName : undefined;
        return {
          groupName: displayName,
          ...(drilldownKey !== undefined && { groupKey: drilldownKey }),
          chartData: [
            { key: "upcoming", label: "Current Month", value: s.upcomingCount, amount: s.upcomingAmount, color: barColors.upcoming },
            { key: "remaining", label: "Beyond Current Month", value: s.remainingCount, amount: s.remainingAmount, color: barColors.remaining },
            { key: "closed", label: "Closed", value: s.closedCount, amount: s.closedAmount, color: barColors.closed },
          ],
        };
      })
      .sort((a, b) =>
        groupKey === "schedule_status"
          ? getTaskScheduleStatusSortRank(a.groupName) - getTaskScheduleStatusSortRank(b.groupName)
          : String(a.groupName).localeCompare(String(b.groupName))
      );
  }

  const groupedCharts =
    dashboardView === "category"
      ? buildGroupedCharts("category")
      : dashboardView === "project"
        ? buildGroupedCharts("project")
        : dashboardView === "owner"
          ? buildGroupedCharts("owner")
          : dashboardView === "schedule_status"
            ? buildGroupedCharts("schedule_status")
            : dashboardView === "vendor"
              ? buildGroupedCharts("vendor")
              : [];

  /** For "By Category" etc: build three bars (upcoming, remaining, closed), each bar = stacked segments by group. */

  const groupNamesForColors = groupedCharts.map((g) => g.groupName);

  const colorByGroupName = (name) => {
    if (dashboardView === "schedule_status") {
      return SCHEDULE_STATUS_COLORS[name] || GROUP_PALETTE[0];
    }

    if (isSimpleView) {
      const groupColors = buildGradientColors(TIME_BLOCKING_CARD_BASE, groupNamesForColors.length);
      const idx = groupNamesForColors.indexOf(name);
      return groupColors[idx] || TIME_BLOCKING_CARD_BASE;
    }

    const groupColors = groupNamesForColors.map((_, idx) => GROUP_PALETTE[idx % GROUP_PALETTE.length]);
    const idx = groupNamesForColors.indexOf(name);
    return groupColors[idx] || GROUP_PALETTE[0];
  };

  function buildStackedBarsData() {
    const upcomingSegments = groupedCharts.map(({ groupName, groupKey, chartData }) => ({
      groupName,
      ...(groupKey !== undefined && { groupKey }),
      value: chartData[0].value,
      amount: chartData[0].amount,
      color: colorByGroupName(groupName),
    }));
    const remainingSegments = groupedCharts.map(({ groupName, groupKey, chartData }) => ({
      groupName,
      ...(groupKey !== undefined && { groupKey }),
      value: chartData[1].value,
      amount: chartData[1].amount,
      color: colorByGroupName(groupName),
    }));
    const closedSegments = groupedCharts.map(({ groupName, groupKey, chartData }) => ({
      groupName,
      ...(groupKey !== undefined && { groupKey }),
      value: chartData[2].value,
      amount: chartData[2].amount,
      color: colorByGroupName(groupName),
    }));
    return { closedSegments, upcomingSegments, remainingSegments };
  }

  const stackedBars = groupedCharts.length > 0 ? buildStackedBarsData() : null;

  const chartData = chartDataAll;
  const maxValue = Math.max(...chartData.map((d) => d.value), 1);

  function tabStyle(isActive) {
    return {
      padding: "8px 14px",
      borderRadius: "999px",
      border: "1px solid " + (isActive ? "rgba(37, 99, 235, 0.35)" : "rgba(203, 213, 225, 0.9)"),
      background: isActive ? "linear-gradient(135deg, #3b82f6, #2563eb)" : "rgba(255,255,255,0.9)",
      color: isActive ? "#fff" : "#334155",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 800,
      boxShadow: isActive ? "0 10px 22px rgba(37, 99, 235, 0.25)" : "0 1px 2px rgba(0,0,0,0.06)",
      transition: "transform 120ms ease-out",
    };
  }

  function setTab(nextTabId) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", nextTabId);
    setSearchParams(next);
  }

  const todayIso = dayjs().format("YYYY-MM-DD");
  const [tbMode, setTbMode] = useState("day"); // 'day' | 'week'
  const [tbSelectedDate, setTbSelectedDate] = useState(todayIso);

  function formatUiDate(isoDate) {
    const d = dayjs(isoDate);
    return d.isValid() ? d.format("MM/DD/YYYY") : String(isoDate || "");
  }

  function formatUiDateShort(isoDate) {
    const d = dayjs(isoDate);
    return d.isValid() ? d.format("ddd, MMM D") : String(isoDate || "");
  }

  function getWeekStartMonday(isoDate) {
    const d = dayjs(isoDate).startOf("day");
    const dow = d.day(); // 0 Sun ... 6 Sat
    const diff = (dow + 6) % 7; // 0 for Monday, 6 for Sunday
    return d.subtract(diff, "day");
  }

  function getDailySummary(isoDate) {
    const scheduledForDay = (items || []).filter((t) => String(t?.schedule_calendar_date || "") === isoDate);
    const pending = scheduledForDay.filter((t) => String(t?.status || "").trim() !== "Closed" && t?.has_passed_time_block !== true);
    // Missed should reflect *scheduled* work that has a passed time block (aligns with calendar/time-block semantics).
    // Completed wins over Missed (a closed task should not show up as missed).
    const missed = scheduledForDay.filter(
      (t) => String(t?.status || "").trim() !== "Closed" && taskHasTimeBlocks(t) && t?.has_passed_time_block === true
    );
    const completed = scheduledForDay.filter((t) => String(t?.status || "").trim() === "Closed");
    return { scheduledForDay, pending, missed, completed };
  }

  function getWeeklySummary(weekStartIso) {
    const start = getWeekStartMonday(weekStartIso);
    const days = Array.from({ length: 7 }).map((_, idx) => start.add(idx, "day").format("YYYY-MM-DD"));
    const perDay = days.map((d) => ({ date: d, ...getDailySummary(d) }));
    return { days, perDay };
  }

  const unscheduledTasks = (items || []).filter((t) => !taskHasTimeBlocks(t) && String(t?.status || "").trim() !== "Closed");
  const scheduledTasks = (items || []).filter((t) => taskHasTimeBlocks(t) && String(t?.status || "").trim() !== "Closed");

  function groupByScheduleStatus(tasks) {
    const groups = {};
    (tasks || []).forEach((t) => {
      const label = getTaskScheduleStatusLabel(t);
      if (!groups[label]) groups[label] = [];
      groups[label].push(t);
    });
    return Object.entries(groups).sort(([a], [b]) => getTaskScheduleStatusSortRank(a) - getTaskScheduleStatusSortRank(b));
  }

  if (authLoading) {
    return (
      <div style={{ minHeight: "calc(100vh - 70px)", padding: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading…
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "calc(100vh - 70px)",
        padding: isSimpleView ? "0 24px 40px" : "32px 24px 40px",
        background: "#f8f9fa",
      }}
    >
      <div
        style={{
          maxWidth: "1120px",
          margin: "0 auto",
        }}
      >
        {isSimpleView ? (
          <div className="page-container--section-tabs" style={{ padding: 0, marginBottom: 4 }}>
            <SectionTabsPageHeader subtitle="Task dashboard and time blocking summary.">
              <TaskSectionTabs activeTab={TASK_TAB_DASHBOARD} />
            </SectionTabsPageHeader>
          </div>
        ) : null}

        <div style={{ height: isSimpleView ? 0 : "6px" }} />

        <ErrorBanner message={displayError} onDismiss={() => setError("")} />

        <div
          style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "16px",
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: "12px",
          }}
        >
          {DASH_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={tabStyle(activeTab === t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && !isSimpleView && (
          <div
            style={{
              display: "flex",
              gap: "6px",
              flexWrap: "wrap",
              marginBottom: "20px",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "12px",
            }}
          >
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setDashboardView(v.id)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "10px",
                  border: "1px solid transparent",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  background:
                    dashboardView === v.id
                      ? "linear-gradient(135deg, #3b82f6, #2563eb)"
                      : "rgba(255,255,255,0.9)",
                  color: dashboardView === v.id ? "#fff" : "#4b5563",
                  boxShadow:
                    dashboardView === v.id
                      ? "0 4px 12px rgba(59, 130, 246, 0.4)"
                      : "0 1px 2px rgba(0,0,0,0.06)",
                }}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === "overview" && (
          <div className="dashboardSimpleGrid">
          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.9)",
              borderRadius: "18px",
              padding: "20px 20px 16px",
              boxShadow:
                "0 18px 45px rgba(15, 23, 42, 0.15), 0 0 0 1px rgba(148, 163, 184, 0.15)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "12px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  Tasks Snapshot
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginTop: "2px",
                  }}
                >
                  {dashboardView === "all"
                    ? "Current month, beyond current month, and closed workload."
                    : `Grouped by ${dashboardView === "category" ? "category" : dashboardView === "project" ? "project" : dashboardView === "owner" ? "owner" : dashboardView === "schedule_status" ? "Schedule Status" : "vendor"}: current month, beyond current month, closed (count + financial).`}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "2px",
                }}
              >
                <div
                  style={{
                    padding: "6px 10px",
                    borderRadius: "999px",
                    fontSize: "11px",
                    backgroundColor: "#eff6ff",
                    color: "#1d4ed8",
                    fontWeight: 600,
                  }}
                >
                  Total: {totalCount} items
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  {formatAmount(totalAmount)}
                </div>
              </div>
            </div>

            {loading ? (
              <div
                style={{
                  padding: "32px 8px 18px",
                  fontSize: "13px",
                  color: "#6b7280",
                  textAlign: "center",
                }}
              >
                Loading dashboard…
              </div>
            ) : displayError ? (
              <div
                style={{
                  padding: "24px 8px 12px",
                  fontSize: "13px",
                  color: "#6b7280",
                  textAlign: "center",
                }}
              >
                <div style={{ marginBottom: "6px" }}>Data could not be loaded.</div>
                <div style={{ fontSize: "12px", color: "#b91c1c", fontWeight: 500 }}>
                  {displayError}
                </div>
              </div>
            ) : dashboardView === "all" ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: "18px",
                    height: "320px",
                    marginTop: "12px",
                    padding: "6px 4px 0",
                  }}
                >
                  {chartData.map((d) => {
                    const ratio = d.value / maxValue;
                    const barHeight = Math.round(48 + ratio * 240);
                    return (
                      <div
                        key={d.key}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleDrilldown(d.key, null)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleDrilldown(d.key, null); } }}
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          cursor: "pointer",
                        }}
                        title={`View tasks: ${d.label} (${d.value})`}
                      >
                        <div style={{ fontSize: "13px", fontWeight: 600, color: "#111827", marginBottom: "2px" }}>{d.value}</div>
                        <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px" }}>{formatAmount(d.amount)}</div>
                        <div
                          style={{
                            width: "44%",
                            minWidth: "36px",
                            height: `${barHeight}px`,
                            borderRadius: "4px 4px 0 0",
                            backgroundColor: d.color,
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
                            transition: "height 450ms ease-out",
                          }}
                        />
                        <div style={{ marginTop: "8px", fontSize: "11px", textAlign: "center", color: "#4b5563", maxWidth: "120px" }}>{d.label}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "14px", fontSize: "11px", color: "#6b7280", borderTop: "1px dashed #e5e7eb", paddingTop: "10px" }}>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {chartData.map((d) => (
                      <div key={d.key} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: d.color, boxShadow: "0 0 0 1px rgba(148, 163, 184, 0.6)" }} />
                        <span>{d.label}</span>
                      </div>
                    ))}
                  </div>
                  {totalCount > 0 && (
                    <div style={{ textAlign: "right" }}>
                      {Math.round((upcomingCount / totalCount) * 100)}% due this month
                    </div>
                  )}
                </div>
              </>
            ) : groupedCharts.length === 0 ? (
              <div style={{ padding: "24px 8px", fontSize: "13px", color: "#6b7280", textAlign: "center" }}>
                No groups to display for this view.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: "18px",
                    height: "320px",
                    marginTop: "12px",
                    padding: "6px 4px 0",
                  }}
                >
                  {[
                    { phaseKey: "upcoming", label: "Current Month", segments: stackedBars.upcomingSegments },
                    { phaseKey: "remaining", label: "Beyond Current Month", segments: stackedBars.remainingSegments },
                    { phaseKey: "closed", label: "Closed", segments: stackedBars.closedSegments },
                  ].map(({ phaseKey, label, segments }) => {
                    const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
                    const barHeight = 280;
                    const minSegmentPx = 6;
                    return (
                      <div
                        key={phaseKey}
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#111827", marginBottom: "2px" }}>{segments.reduce((s, seg) => s + seg.value, 0)}</div>
                        <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>{formatAmount(segments.reduce((s, seg) => s + seg.amount, 0))}</div>
                        <div
                          style={{
                            width: "44%",
                            minWidth: "36px",
                            height: `${barHeight}px`,
                            borderRadius: "4px 4px 0 0",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column-reverse",
                            border: "1px solid #e5e7eb",
                            backgroundColor: "#f9fafb",
                            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
                          }}
                        >
                          {segments.map((seg) => {
                            const rawHeight = total > 0 ? (seg.value / total) * barHeight : 0;
                            const segmentHeight = seg.value > 0 ? Math.max(rawHeight, minSegmentPx) : 0;
                            return (
                              <div
                                key={seg.groupName}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleDrilldown(phaseKey, seg.groupKey ?? seg.groupName)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleDrilldown(phaseKey, seg.groupKey ?? seg.groupName);
                                  }
                                }}
                                style={{
                                  height: `${segmentHeight}px`,
                                  minHeight: segmentHeight > 0 ? "6px" : 0,
                                  backgroundColor: seg.color,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  borderTop: "1px solid rgba(255,255,255,0.5)",
                                }}
                                title={`${seg.groupName}: ${seg.value} · ${formatAmount(seg.amount)} — View tasks`}
                              />
                            );
                          })}
                        </div>
                        <div style={{ marginTop: "8px", fontSize: "11px", textAlign: "center", color: "#4b5563", maxWidth: "120px" }}>{label}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "14px", fontSize: "11px", color: "#6b7280", borderTop: "1px dashed #e5e7eb", paddingTop: "10px" }}>
                  {groupedCharts.map(({ groupName }) => {
                    const color = colorByGroupName(groupName);
                    return (
                      <div key={groupName} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: color, flexShrink: 0 }} />
                        <span>{groupName}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            <div
              style={{
                background: "#003153",
                borderRadius: "24px",
                padding: "18px 18px 16px",
                color: "#e5e7eb",
                boxShadow:
                  "0 10px 15px -3px rgba(0, 49, 83, 0.4)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: "-40%",
                  background:
                    "radial-gradient(circle at 15% 0%, rgba(0,75,125,0.9), transparent 60%), radial-gradient(circle at 85% 100%, rgba(0,75,125,0.35), transparent 55%)",
                  opacity: 1,
                }}
              />
              <div style={{ position: "relative", zIndex: 1 }}>
                <div
                  style={{
                    fontSize: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: "#bfdbfe",
                    marginBottom: "6px",
                    fontWeight: 700,
                  }}
                >
                  Next Action:
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 800,
                    marginBottom: "6px",
                    lineHeight: 1.2,
                    color: "#ffffff",
                  }}
                >
                  Time Blocking
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#dbeafe",
                    marginBottom: "14px",
                    lineHeight: 1.5,
                  }}
                >
                  Take the next step by quickly allocate time for your critical tasks.
                  Click below to enter the full Time Blocking interface.
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/TimeBlocking")}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "12px",
                    background: "#004B7D",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.25)",
                    cursor: "pointer",
                    fontWeight: 800,
                    fontSize: "13px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  title="Plan your week with Time Blocking"
                >
                  Plan Your Week with Time Blocking
                </button>
              </div>
            </div>

            <div
              style={{
                background:
                  "linear-gradient(135deg, #0f172a, #1e293b, #0b1120)",
                borderRadius: "18px",
                padding: "18px 18px 16px",
                color: "#e5e7eb",
                boxShadow:
                  "0 18px 40px rgba(15, 23, 42, 0.5), 0 0 0 1px rgba(148, 163, 184, 0.35)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: "-40%",
                  background:
                    "radial-gradient(circle at 0 0, rgba(59,130,246,0.4), transparent 60%), radial-gradient(circle at 100% 100%, rgba(236,72,153,0.45), transparent 55%)",
                  opacity: 0.7,
                }}
              />
              <div style={{ position: "relative", zIndex: 1 }}>
                <div
                  style={{
                    fontSize: "13px",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: "#9ca3af",
                    marginBottom: "4px",
                  }}
                >
                  Focus
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                >
                  Your workload at a glance
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#d1d5db",
                    marginBottom: "10px",
                  }}
                >
                  Use the dashboard to quickly see what&apos;s done, what&apos;s
                  coming soon, and what still needs attention.
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDrilldown("upcoming", null)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleDrilldown("upcoming", null); } }}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "12px",
                      backgroundColor: "rgba(15,23,42,0.7)",
                      border: `1px solid ${barColors.upcoming}`,
                      cursor: "pointer",
                    }}
                    title="View tasks in the current month"
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: barColors.upcoming,
                        marginBottom: "2px",
                      }}
                    >
                      Current Month
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: barColors.upcoming,
                      }}
                    >
                      {upcomingCount}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "2px",
                      }}
                    >
                      {formatAmount(upcomingAmount)}
                    </div>
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDrilldown("remaining", null)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleDrilldown("remaining", null); } }}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "12px",
                      backgroundColor: "rgba(15,23,42,0.7)",
                      border: `1px solid ${barColors.remaining}`,
                      cursor: "pointer",
                    }}
                    title="View tasks beyond the current month"
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: barColors.remaining,
                        marginBottom: "2px",
                      }}
                    >
                      Beyond Current Month
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: barColors.remaining,
                      }}
                    >
                      {remainingCount}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "2px",
                      }}
                    >
                      {formatAmount(remainingAmount)}
                    </div>
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDrilldown("closed", null)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleDrilldown("closed", null); } }}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "12px",
                      backgroundColor: "rgba(15,23,42,0.7)",
                      border: `1px solid ${barColors.closed}`,
                      cursor: "pointer",
                    }}
                    title="View closed tasks"
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginBottom: "2px",
                      }}
                    >
                      Closed
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: barColors.closed,
                      }}
                    >
                      {closedCount}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "2px",
                      }}
                    >
                      {formatAmount(closedAmount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {activeTab === "time_blocking" && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "16px" }}>
            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.9)",
                borderRadius: "18px",
                padding: "18px",
                boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.12)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 800, color: "#111827" }}>Time Blocking Summary</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                    Unscheduled backlog + scheduled work. Drill from Daily to Weekly view.
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  <button type="button" onClick={() => navigate("/TimeBlocking")} style={{ ...tabStyle(false), padding: "8px 12px" }}>
                    Open Time Blocking
                  </button>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button type="button" onClick={() => setTbMode("day")} style={tabStyle(tbMode === "day")}>Daily</button>
                    <button type="button" onClick={() => { setTbMode("week"); }} style={tabStyle(tbMode === "week")}>Weekly</button>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontSize: "12px", fontWeight: 800, color: "#334155" }}>Selected date</div>
                  <input
                    type="date"
                    value={tbSelectedDate}
                    onChange={(e) => setTbSelectedDate(e.target.value)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  />
                </div>
                {tbMode === "week" && (
                  <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 800 }}>
                    {(() => {
                      const weekStart = getWeekStartMonday(tbSelectedDate);
                      const weekEnd = weekStart.add(6, "day");
                      return (
                        <>
                          {weekStart.format("MMM D")} – {weekEnd.format("MMM D, YYYY")}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {tbMode === "day" ? (
                (() => {
                  const s = getDailySummary(tbSelectedDate);
                  return (
                    <div style={{ marginTop: "14px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px" }}>
                      {[
                        { key: "pending", label: "Pending", value: s.pending.length, color: "#2563eb" },
                        { key: "missed", label: "Missed", value: s.missed.length, color: SCHEDULE_STATUS_COLORS.Missed },
                        { key: "completed", label: "Completed", value: s.completed.length, color: SCHEDULE_STATUS_COLORS.Completed },
                      ].map((x) => (
                        <div
                          key={x.key}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const params = new URLSearchParams();
                            params.set("scheduleDate", tbSelectedDate);
                            if (x.key === "missed") params.set("scheduleStatus", "missed");
                            if (x.key === "completed") params.set("status", "closed");
                            navigate("/Task?" + params.toString());
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              const params = new URLSearchParams();
                              params.set("scheduleDate", tbSelectedDate);
                              if (x.key === "missed") params.set("scheduleStatus", "missed");
                              if (x.key === "completed") params.set("status", "closed");
                              navigate("/Task?" + params.toString());
                            }
                          }}
                          style={{
                            padding: "12px 14px",
                            borderRadius: "16px",
                            background: "rgba(248, 250, 252, 0.9)",
                            border: "1px solid rgba(148, 163, 184, 0.25)",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ fontSize: "12px", fontWeight: 800, color: "#64748b" }}>{x.label}</div>
                          <div style={{ fontSize: "26px", fontWeight: 900, color: x.color, marginTop: "4px" }}>{x.value}</div>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>Scheduled on {formatUiDate(tbSelectedDate)}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              ) : (
                (() => {
                  const week = getWeeklySummary(tbSelectedDate);
                  return (
                    <div style={{ marginTop: "14px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "2px" }}>
                        {week.perDay.map((d) => (
                          <button
                            key={d.date}
                            type="button"
                            onClick={() => { setTbSelectedDate(d.date); setTbMode("day"); }}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "140px repeat(3, minmax(0, 1fr))",
                              gap: "8px",
                              alignItems: "center",
                              width: "100%",
                              textAlign: "left",
                              padding: "8px 10px",
                              borderRadius: "12px",
                              border: "1px solid rgba(148, 163, 184, 0.25)",
                              background: "rgba(255,255,255,0.95)",
                              cursor: "pointer",
                            }}
                            title="Click to drill into that day"
                          >
                            <div style={{ fontWeight: 900, color: "#111827", fontSize: "12px" }}>{formatUiDateShort(d.date)}</div>
                            <div style={{ fontWeight: 900, color: "#2563eb", fontSize: "12px" }}>Pending: {d.pending.length}</div>
                            <div style={{ fontWeight: 900, color: SCHEDULE_STATUS_COLORS.Missed, fontSize: "12px" }}>Missed: {d.missed.length}</div>
                            <div style={{ fontWeight: 900, color: SCHEDULE_STATUS_COLORS.Completed, fontSize: "12px" }}>Completed: {d.completed.length}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {tbMode === "day" && (
            <div className="dashboardSimpleGrid" style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}>
              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.9)",
                  borderRadius: "18px",
                  padding: "18px",
                  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.12)",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: 900, color: "#111827", marginBottom: "6px" }}>
                  Unscheduled tasks (backlog)
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "12px" }}>
                  No time blocks yet. Status reflects urgency (e.g., Overdue).
                </div>
                {loading ? (
                  <div style={{ padding: "18px 6px", fontSize: "13px", color: "#6b7280" }}>Loading…</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "8px" }}>
                    {groupByScheduleStatus(unscheduledTasks).map(([label, list]) => (
                      <div
                        key={label}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate("/Task?scheduleStatus=" + scheduleStatusLabelToParam(label))}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/Task?scheduleStatus=" + scheduleStatusLabelToParam(label)); } }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                          padding: "10px 12px",
                          borderRadius: "14px",
                          border: "1px solid rgba(148, 163, 184, 0.22)",
                          background: "rgba(248, 250, 252, 0.9)",
                          cursor: "pointer",
                        }}
                        title="Open tasks list filtered by schedule status"
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: SCHEDULE_STATUS_COLORS[label] || "#94a3b8" }} />
                          <span style={{ fontWeight: 900, color: "#0f172a", fontSize: "13px" }}>{label}</span>
                        </div>
                        <div style={{ fontWeight: 900, color: "#111827" }}>{list.length}</div>
                      </div>
                    ))}
                    {unscheduledTasks.length === 0 && (
                      <div style={{ padding: "10px 6px", color: "#6b7280", fontSize: "13px" }}>No unscheduled tasks.</div>
                    )}
                  </div>
                )}
              </div>

              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.9)",
                  borderRadius: "18px",
                  padding: "18px",
                  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.12)",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: 900, color: "#111827", marginBottom: "6px" }}>
                  Scheduled tasks (with time blocks)
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "12px" }}>
                  Work already placed on the calendar. Status reflects execution risk (e.g., Missed).
                </div>
                {loading ? (
                  <div style={{ padding: "18px 6px", fontSize: "13px", color: "#6b7280" }}>Loading…</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "8px" }}>
                    {groupByScheduleStatus(scheduledTasks).map(([label, list]) => (
                      <div
                        key={label}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate("/Task?scheduleStatus=" + scheduleStatusLabelToParam(label))}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/Task?scheduleStatus=" + scheduleStatusLabelToParam(label)); } }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                          padding: "10px 12px",
                          borderRadius: "14px",
                          border: "1px solid rgba(148, 163, 184, 0.22)",
                          background: "rgba(248, 250, 252, 0.9)",
                          cursor: "pointer",
                        }}
                        title="Open tasks list filtered by schedule status"
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: SCHEDULE_STATUS_COLORS[label] || "#94a3b8" }} />
                          <span style={{ fontWeight: 900, color: "#0f172a", fontSize: "13px" }}>{label}</span>
                        </div>
                        <div style={{ fontWeight: 900, color: "#111827" }}>{list.length}</div>
                      </div>
                    ))}
                    {scheduledTasks.length === 0 && (
                      <div style={{ padding: "10px 6px", color: "#6b7280", fontSize: "13px" }}>No scheduled tasks.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;

